/** *******************************************************************************************************************
 * Copyright (c) 2020 - Fundação CERTI
 * All rights reserved.
 ******************************************************************************************************************* */

import React, { useCallback, useRef, useState, useMemo, useEffect } from 'react';
import { Autocomplete } from '@material-ui/lab';
import { withRouter } from 'react-router-dom';
import Papa from 'papaparse';
import XLSX from 'xlsx';

import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Button,
  Box,
  Typography,
  TextField,
} from '@material-ui/core';
import * as firebase from 'firebase/app';

import Modal from '../../core/Modal';
import Scaffold from '../../core/Scaffold/Scaffold';
import { validateRow, getLogErrors, getMaskedRow, isEmptyLine, hasEmptyEmails } from '../../core/fieldValidator';
import { getUsersByEmails, saveUsersInFirestore } from '../../services/csv';
import { convertKeys, getAddress, removeAddress } from '../../utils/user';
import { parseDate } from '../../core/dateParser';
import useStyles from './styles';
import { getFileExtension } from '../../core/utils/helpers';
import importConfig from '../../utils/importConfig';

const db = firebase.firestore();

const Import = () => {
  const classes = useStyles();
  const [companiesList, setCompaniesList] = useState([]);
  const [fileName, setFileName] = useState(null);
  const [company, setCompany] = useState(null);
  const [data, setData] = useState([]);
  const [emptyEmailsModal, setEmptyModalImport] = useState(null);
  const [progress, setProgress] = useState({
    isLoading: false,
    value: 0,
  });
  const fileRef = useRef(null);

  const getRowIsValid = useCallback((row) => validateRow(row)?.length <= 0, []);

  const validData = useMemo(() => data?.filter(getRowIsValid), [data, getRowIsValid]);

  const errorFile = useMemo(
    () => {
      if (!data) return null;
      const errors = getLogErrors(data);
      if (!errors) return null;

      const blob = new Blob([errors], { type: 'text/plain' });
      return URL.createObjectURL(blob);
    },
    [data]
  );

  useEffect(() => {
    const getCompanies = async () => {
      const companies = (await db.collection('companies').get()).docs;
      setCompaniesList(
        companies
          ?.map((company) => ({
            id: company.id,
            ...company.data(),
          }))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
    };

    getCompanies();
  }, []);

  const handleAddFile = useCallback(() => {
    fileRef.current.click();
  }, []);

  const addMissingEmails = useCallback(async () => {
    setData(null);
    const convertedResult = data.map((convertedKeys) => {
      const maskedRow = getMaskedRow(convertedKeys, true);
      if (!maskedRow) return convertedKeys;
      return maskedRow;
    });
    setData(convertedResult);
  }, [data]);

  const validateParsedData = useCallback(
    async (results) => {
      if (!results) return;

      // Convert keys.
      let convertedResult = results.data.map((item) => {
        const convertedKeys = convertKeys(item);
        const maskedRow = getMaskedRow(convertedKeys, false);
        if (!maskedRow) return convertedKeys;
        return maskedRow;
      });
      convertedResult = convertedResult.filter((item) => !isEmptyLine(item));

      const empty = hasEmptyEmails(convertedResult);
      if (empty > 0) {
        setEmptyModalImport(empty);
      }
      setData(convertedResult);
    }, []
  );

  const parseFile = useCallback(
    async ({ file, changeEmail }) => {
      if (!file) return;
      const inputData = getFileExtension(file.name) === 'csv' ? file : await convertXlsxToCsv(file);
      Papa.parse(inputData, {
        ...importConfig,
        complete: (results) => {
          validateParsedData(results);
        },
      });
    },
    [validateParsedData]
  );

  async function convertXlsxToCsv(file) {
    const result = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (file) => {
        const data = file.target.result;
        const readData = XLSX.read(data, { type: 'binary' });
        const sheetName = readData.SheetNames[0];
        const sheetData = readData.Sheets[sheetName];
        const dataInput = XLSX.utils.sheet_to_csv(sheetData, { header: 1 });
        resolve(dataInput);
      };
      reader.readAsBinaryString(file);
    });
    return result;
  }

  const handleChangeFile = useCallback(
    async (event) => {
      const newFile = event.target.files[0];
      event.target.value = null;
      setFileName(newFile?.name);
      if (!newFile) return;

      parseFile({ file: newFile })
    },
    [parseFile]
  );

  const getOptionSelected = useCallback((option, value) => {
    const { name: clicked } = option;
    const { name: selected } = value;
    if (clicked === selected) {
      setCompany(option);
      return true;
    }
    return false;
  }, []);

  const handleChangeCompany = useCallback(
    (event) => {
      const { value } = event.target;

      if (!companiesList.find((item) => item.name === value)) {
        setCompany(null);
      }
    },
    [companiesList]
  );

  const renderLoading = useMemo(
    () => {
      if (progress.value !== 100 && progress.isLoading === false) return null;

      if (progress.value === 100) {
        setTimeout(() => setProgress({ value: 0, isLoading: false }), 1000);
      }

      return (
        <Box position="relative">
          <CircularProgress variant="static" value={progress.value.toFixed(0)} />
          <Box
            top={0}
            left={0}
            bottom={0}
            right={0}
            position="absolute"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Typography variant="caption" component="div" color="textSecondary">
              {`${progress.value.toFixed(0)}%`}
            </Typography>
          </Box>
        </Box>
      );
    },
    [progress]
  );

  const handleSendFile = useCallback(
    () => {
      if (progress.isLoading || validData.length < 1) return;

      setProgress({ value: 2, isLoading: true });

      const callApi = async () => {
        const users = await getUsersByEmails(validData.map((item) => item.email));

        // Convert keys of all items and save the id.
        const convertedData = validData.map((item) => {
          let convertedItem = {
            ...item,
            // Add user id created by API.
            id: users[item.email],
            // Parse date to firesbase input.
            birthdate: parseDate(item.birthdate),
          };
          // Split address.
          convertedItem.address = getAddress(convertedItem);
          // Remove redundant Address.
          convertedItem = removeAddress(convertedItem);

          return convertedItem;
        });

        saveUsersInFirestore(company.id, convertedData, setProgress);
      };

      callApi();
    },
    [progress, validData, company, setProgress]
  );

  const handleCancelCreateEmail = useCallback(
    async () => {
      setEmptyModalImport(false);
    },
    []
  );

  const handleConfirmCreateEmail = useCallback(
    async () => {
      setEmptyModalImport(false);
      addMissingEmails();
    },
    [addMissingEmails]
  );

  const renderItemValidation = useCallback((item) => {
    const row = validateRow(item);
    if (!row) return null;

    const hasError = row.length > 0;

    const value = !hasError ? '✓' : row[0].errors;

    const validationStyle = { color: !hasError ? 'green' : 'red', fontWeight: 'bold' };
    const renderItem = (error) => (
      <span key={error}>
        {error}
        <br />
      </span>
    );
    return (
      <TableCell align="center" style={validationStyle}>
        {hasError ? value.map(renderItem) : value}
      </TableCell >
    );
  }, []);

  const renderTable = useCallback(
    () => {
      if (!data?.length) {
        return null;
      }
      return (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nome do paciente</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>CPF</TableCell>
              <TableCell align="center">Validação</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item) => (
              <TableRow key={item.email || item.cpf}>
                <TableCell>{item.fullname}</TableCell>
                <TableCell>{item.email}</TableCell>
                <TableCell>{item.documentId}</TableCell>
                {renderItemValidation(item)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      );
    },
    [data, renderItemValidation]
  );

  const renderImportButton = useCallback(
    () => (
      <Button
        type="button"
        onClick={handleSendFile}
        disabled={!!errorFile || !!(data?.length < 1) || !company || progress.isLoading}
        classes={{
          root: classes['import-button'],
          disabled: classes['import-button-disabled'],
        }}
      >
        {progress.isLoading ? 'Aguarde ...' : 'Importar'}
      </Button>
    ),
    [classes, company, data, errorFile, handleSendFile, progress.isLoading]
  );

  return (
    <Scaffold title="Importar tabela de usuários" renderRightComponent={renderImportButton}>
      <h1>Importar</h1>
      <div className={classes.buttons}>
        <Autocomplete
          id="company-select"
          style={{ width: 300 }}
          className={classes.autocomplete}
          autoHighlight
          onChange={handleChangeCompany}
          getOptionSelected={getOptionSelected}
          getOptionLabel={(option) => option.name}
          options={companiesList}
          renderInput={(params) => <TextField {...params} label="Empresas" variant="outlined" />}
        />
        {companiesList.length > 0 && (
          <>
            <input
              type="file"
              name="file-spread "
              ref={fileRef}
              id="file-spread"
              accept=".csv,.xls,.xlsx"
              onChange={handleChangeFile}
            />
            <div className={classes.uploadContainer}>
              <Button type="button" variant="outlined" onClick={handleAddFile} className={classes.uploadButton}>
                SELECIONAR ARQUIVO...
              </Button>
              {fileName}
              {renderLoading}
            </div>
          </>
        )}
      </div>
      {errorFile && (
        <a className={classes.link} download="log-error.txt" href={errorFile}>
          Baixar log
        </a>
      )}

      {renderTable()}
      <br />

      {
        emptyEmailsModal && (
          <Modal
            title={`Há ${Number(emptyEmailsModal) || ''} usuários sem email`}
            ariaLabel="Há usuários sem email, vamos usar um padrão no email. Deseja continuar?"
            onClose={handleCancelCreateEmail}
            onConfirm={handleConfirmCreateEmail}
            confirmLabel="CONTINUAR"
            cancelLabel="CANCELAR"
          >
            <p>
              Se você continuar, vamos criar um email com a estrutura
            <b> xxx.xxx.xxx-xx@iniciativacovid19.com.br </b>
            (usando o CPF do usuário) para cada usuário sem email.
          </p>
          </Modal>
        )
      }
    </Scaffold >
  );
};

export default withRouter(Import);
